/* Cart count badge sync.

   snippets/cart-icon-link.liquid renders three cart icons:
     #cart-icon-bubble         .header__icon-desktop  — visible >=990px
     #cart-icon-bubble-mobile  .header__icon-mobile   — visible <990px
     #nav-drawer-cart-icon     nav drawer bottom row
   ...but every AJAX cart path renders the `cart-icon-bubble` SECTION into
   #cart-icon-bubble alone (cart.js, cart-drawer.js, cart-notification.js,
   quick-add-bulk.js, quick-order-list.js, cart-discount.js and
   cart-checkbox-upsell.js all resolve it by that id). Below 990px that anchor
   is display:none, so the badge the shopper actually sees never moved off its
   page-load value. Worse: the bubble is gated on `cart != empty`, so a page
   loaded with an empty cart carried no .cart-count-bubble node at all and
   adding an item left the mobile badge missing until a full reload.

   The three icons carry different SVGs (snippets/icon-cart.liquid switches on
   `mobile` / `mobile_open`), so the section HTML cannot be copied wholesale —
   only the .cart-count-bubble node is mirrored.

   Driven by a MutationObserver on #cart-icon-bubble rather than
   PUB_SUB_EVENTS.cartUpdate: wishlist-modal.js and the two quick-add paths in
   theme.js call cartDrawer.renderContents() without publishing cartUpdate, but
   all of them rewrite that node. cartUpdate is kept as a backstop for cart
   mutations that carry no section HTML.
*/
(() => {
  const CART_ICONS = '#cart-icon-bubble, #cart-icon-bubble-mobile, #nav-drawer-cart-icon';
  const BUBBLE = '.cart-count-bubble';

  const icons = () => document.querySelectorAll(CART_ICONS);

  /* Clone the freshly rendered bubble onto every icon; a null source means the
     cart is empty and the node has to go. Cloning rather than writing a number
     keeps the localized `sections.header.cart_count` string and its
     pluralization intact — JS has no access to the translation. */
  const applyBubble = (sourceBubble) => {
    icons().forEach((anchor) => {
      const current = anchor.querySelector(BUBBLE);
      if (!sourceBubble) {
        current?.remove();
        return;
      }
      if (!current) {
        anchor.appendChild(sourceBubble.cloneNode(true));
      } else if (current !== sourceBubble && current.innerHTML !== sourceBubble.innerHTML) {
        current.innerHTML = sourceBubble.innerHTML;
      }
    });
  };

  const displayedCount = () => {
    for (const anchor of icons()) {
      const label = anchor.querySelector(`${BUBBLE} [aria-hidden="true"]`);
      if (label) return parseInt(label.textContent.trim(), 10) || 0;
    }
    return 0;
  };

  /* No section HTML to mirror (bfcache restore, or a cart mutation that skipped
     the sections param) — ask the server for the section on its own. */
  let inFlight = false;
  const refreshFromServer = () => {
    if (inFlight || !icons().length) return;
    inFlight = true;

    const base = typeof getSectionsUrl === 'function' ? getSectionsUrl() : window.location.pathname;
    const url = `${base}${base.includes('?') ? '&' : '?'}section_id=cart-icon-bubble`;

    /* cache: 'no-store' is load-bearing. The whole reason this runs is that the
       browser answered Back from its disk cache; a plain fetch of the section
       URL is answered from that same cache and hands back the identical stale
       badge, so the refresh silently achieves nothing. Verified on qual: the
       add direction appeared fixed only because that URL had not been requested
       before — the reverse direction (cart emptied while away) still showed the
       phantom count until this was added. */
    fetch(url, { cache: 'no-store' })
      .then((response) => (response.ok ? response.text() : Promise.reject(new Error(response.status))))
      .then((html) => {
        applyBubble(new DOMParser().parseFromString(html, 'text/html').querySelector(BUBBLE));
      })
      .catch(() => {})
      .finally(() => {
        inFlight = false;
      });
  };

  const desktopIcon = document.getElementById('cart-icon-bubble');
  if (desktopIcon) {
    /* applyBubble only ever writes to the OTHER icons — the source node is
       skipped by identity — so this cannot re-trigger itself into a loop.
       subtree:true is required because cart-checkbox-upsell.js injects the raw
       section response, leaving the bubble nested under a .shopify-section. */
    new MutationObserver(() => applyBubble(desktopIcon.querySelector(BUBBLE))).observe(desktopIcon, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  /* A restored page hands the DOM back as it was left, so the badge still shows
     the cart as of the last paint — stale whenever the shopper added something
     on the page they just came Back from.

     AAS-5514: neither `event.persisted` nor the navigation type can decide this.
     persisted is true only for a BFCACHE restore, while Chrome commonly answers
     Back from the HTTP disk cache; and the navigation type is not reliable
     either — measured on qual, mobile, the restored page reported "navigate"
     with a non-zero transferSize while its markup was demonstrably stale.
     Desktop reported "back_forward" for the same flow, which is exactly why the
     bug read as desktop-only.

     So compare against the cart itself and only touch the DOM on a genuine
     disagreement. One small uncached /cart.js per page show; no section render
     and no DOM write when the badge is already right. */
  const reconcile = async () => {
    if (!icons().length) return;
    const root = typeof routes !== 'undefined' && routes.cart_url ? routes.cart_url : '/cart';
    const cart = await fetch(`${root}.js`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
    if (!cart || typeof cart.item_count !== 'number') return;
    if (cart.item_count !== displayedCount()) refreshFromServer();
  };

  window.addEventListener('pageshow', reconcile);

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      const count = event?.cartData?.item_count;
      // /cart/add.js resolves to the added line item, which carries no
      // item_count — those paths are covered by the observer above.
      if (typeof count !== 'number') return;

      // Let a section-render path land first; only go to the server if the
      // badge still disagrees with the authoritative count.
      setTimeout(() => {
        if (displayedCount() !== count) refreshFromServer();
      }, 100);
    });
  }
})();
